"use client";

import {
  Box,
  Button,
  Field,
  HStack,
  Input,
  Stack,
  Text,
  Textarea,
} from "@chakra-ui/react";
import { useCallback } from "react";
import { useForm } from "react-hook-form";
import type { Origin, PutOriginRequest } from "@/generated/fastapi";
import { showErrorToast } from "@/shared/feedback";
import GenericIcon from "@/shared/ui/actions/GenericIcon";
import appSettings from "@/shared/config/appSettings";
import useOriginActions from "../hooks/useOriginActions";
import type { EditableOriginInput } from "../types";

type OriginEditFormValues = {
  originContent: string;
  userNote?: string;
};

type OriginEditFormProps = {
  originToEdit: EditableOriginInput;
  onCancel?: () => void;
  onSuccess?: (origin: Origin) => void;
};

export default function OriginEditForm({
  originToEdit,
  onCancel,
  onSuccess,
}: OriginEditFormProps) {
  const normalizeOptionalNote = useCallback((value?: string | null) => {
    const normalized = value?.trim() ?? "";
    return normalized.length > 0 ? normalized : null;
  }, []);
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<OriginEditFormValues>({
    defaultValues: {
      originContent: originToEdit.raw_text_input,
      userNote: originToEdit.user_note ?? "",
    },
  });
  const { handleUpdateOriginClick } = useOriginActions();
  const originContentValue = watch("originContent");
  const userNoteValue = watch("userNote");

  const detectChanges = useCallback(
    (data: OriginEditFormValues) => {
      const nextUserNote = normalizeOptionalNote(data.userNote);
      const currentUserNote = normalizeOptionalNote(originToEdit.user_note);

      return (
        data.originContent !== originToEdit.raw_text_input ||
        nextUserNote !== currentUserNote
      );
    },
    [normalizeOptionalNote, originToEdit],
  );
  const currentFormValues = {
    originContent: originContentValue ?? "",
    userNote: userNoteValue ?? "",
  };
  const cleanedOriginContent = currentFormValues.originContent.trim();
  const isContentTooShort =
    cleanedOriginContent.length > 0 &&
    cleanedOriginContent.length < appSettings.validation.ORIGIN_MIN_LENGTH;
  const hasChanges = detectChanges(currentFormValues);
  const saveDisabledReason =
    cleanedOriginContent.length === 0
      ? "Add source material before saving."
      : isContentTooShort
        ? `Content must be at least ${appSettings.validation.ORIGIN_MIN_LENGTH} characters.`
        : !hasChanges
          ? "Make a change before saving."
          : null;
  const isSaveDisabled = isSubmitting || Boolean(saveDisabledReason);

  const onSubmit = handleSubmit(async (data) => {
    if (!detectChanges(data)) {
      return;
    }

    try {
      const nextUserNote = normalizeOptionalNote(data.userNote);
      const currentUserNote = normalizeOptionalNote(originToEdit.user_note);
      const requestData: PutOriginRequest = {
        origin_id: originToEdit.id,
        edited_text_input:
          originToEdit.raw_text_input !== data.originContent
            ? data.originContent
            : undefined,
        user_note: nextUserNote !== currentUserNote ? nextUserNote : undefined,
      };

      const updatedOrigin = await handleUpdateOriginClick(
        requestData,
        (origin) => {
          onSuccess?.(origin);
          return origin;
        },
      );

      if (!updatedOrigin) {
        throw new Error("Update failed");
      }
    } catch (error) {
      console.error("Error during origin update:", error);
      showErrorToast("Failed to update source material");
    }
  });

  return (
    <form onSubmit={onSubmit}>
      <Stack gap="4" align="stretch">
        <Field.Root invalid={!!errors.originContent}>
          <Field.Label>Content</Field.Label>
          <Textarea
            autoresize
            maxH="3xs"
            minH="3xs"
            placeholder="Source material content"
            {...register("originContent", {
              required: "Must provide content to process",
              minLength: {
                value: appSettings.validation.ORIGIN_MIN_LENGTH,
                message: `Content must be at least ${appSettings.validation.ORIGIN_MIN_LENGTH} characters`,
              },
            })}
          />
          <Field.HelperText>
            Text content that you want to process for your career profile
          </Field.HelperText>
          <Field.ErrorText>{errors.originContent?.message}</Field.ErrorText>
        </Field.Root>

        <Field.Root>
          <Field.Label>Note (optional)</Field.Label>
          <Input
            placeholder="Something to note ... "
            {...register("userNote")}
          />
          <Field.HelperText>
            Note for your reference (not processed, optional)
          </Field.HelperText>
        </Field.Root>

        {saveDisabledReason ? (
          <Text fontSize="xs" color="moveon.text.muted">
            {saveDisabledReason}
          </Text>
        ) : null}

        <HStack gap="2" align="center" justify="space-between" width="full">
          {onCancel ? (
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              aria-label="Cancel editing"
              minW={{ base: "10", sm: "auto" }}
            >
              <GenericIcon icon="return" />
              <Text as="span" display={{ base: "none", sm: "inline" }}>
                Return
              </Text>
            </Button>
          ) : (
            <Box />
          )}

          <HStack gap="2" justify="flex-end">
            <Button
              type="submit"
              bg="moveon.primary"
              loading={isSubmitting}
              loadingText="Saving"
              disabled={isSaveDisabled}
              aria-label="Save source material"
              minW={{ base: "10", sm: "auto" }}
            >
              <GenericIcon icon="edit" />
              <Text as="span" display={{ base: "none", sm: "inline" }}>
                Save
              </Text>
            </Button>
          </HStack>
        </HStack>
      </Stack>
    </form>
  );
}
